import { CourseTimeResponseSchema, CourseTimeResponseType } from '@/types/timetable';

const COURSE_TIME_REGEX = /([월화수목금토일])\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/;
const CLASSROOM_REGEX = /\(([^)]+)\)/;
const MINUTES_PER_DAY = 24 * 60;
const TIME_STEP_MINUTES = 5;

const toMinutes = (time: string) => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
};

const formatTime = (totalMinutes: number) => {
  const normalized = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hour = Math.floor(normalized / 60)
    .toString()
    .padStart(2, '0');
  const minute = (normalized % 60).toString().padStart(2, '0');
  return `${hour}:${minute}`;
};

const roundToFiveMinutes = (totalMinutes: number) => {
  return Math.round(totalMinutes / TIME_STEP_MINUTES) * TIME_STEP_MINUTES;
};

export const normalizeTimeToFiveMinutes = (time: string) => {
  return formatTime(roundToFiveMinutes(toMinutes(time)));
};

export const normalizeCourseTimeRangeToFiveMinutes = (startTime: string, endTime: string) => {
  const startMinutes = roundToFiveMinutes(toMinutes(startTime));
  const endMinutes = roundToFiveMinutes(toMinutes(endTime));
  const normalizedEndMinutes =
    endMinutes > startMinutes ? endMinutes : startMinutes + TIME_STEP_MINUTES;

  return {
    start: formatTime(startMinutes),
    end: formatTime(normalizedEndMinutes),
  };
};

export const parseCourseScheduleRoom = (scheduleRoom?: null | string): CourseTimeResponseType[] => {
  if (!scheduleRoom) {
    return [];
  }

  return scheduleRoom
    .split('\n')
    .flatMap((line) => line.split(','))
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const timeMatch = line.match(COURSE_TIME_REGEX);
      if (!timeMatch) {
        return null;
      }

      const [, week, startTime, endTime] = timeMatch;
      const classroomMatch = line.match(CLASSROOM_REGEX);
      const normalizedRange = normalizeCourseTimeRangeToFiveMinutes(startTime, endTime);

      return CourseTimeResponseSchema.parse({
        week,
        start: normalizedRange.start,
        end: normalizedRange.end,
        classroom: classroomMatch?.[1] ?? '',
      });
    })
    .filter((value): value is CourseTimeResponseType => value !== null);
};

export const formatCourseTimeSummary = (courseTimes: CourseTimeResponseType[]) => {
  if (courseTimes.length === 0) {
    return '시간 미정';
  }

  const { week, start, end } = courseTimes[0];
  return `${week} ${start} ~ ${end}`;
};
